/**
 * Share report files (CSV/PDF) via native share sheet
 * Enables SMS, WhatsApp, Messenger, Email, etc.
 */

import { Platform, Share } from 'react-native';
import type { ExposureLogEntry } from '../types';
import { generateLogCsv, generateLogPdfHtml } from './exportLogService';

export async function shareCsvReport(
  entries: ExposureLogEntry[],
  onError: (msg: string) => void
): Promise<boolean> {
  if (Platform.OS === 'web') {
    return false; // Web uses download, not share
  }

  try {
    const RNFS = require('react-native-fs');
    const csv = generateLogCsv(entries);
    const filename = `fvrad-exposure-${new Date().toISOString().slice(0, 10)}.csv`;
    const path = `${RNFS.CachesDirectoryPath}/${filename}`;

    await RNFS.writeFile(path, csv, 'utf8');

    await Share.share({
      url: Platform.OS === 'ios' ? `file://${path}` : path,
      message: `FVrad exposure log - ${entries.length} entries`,
      title: 'FVrad Exposure Log (CSV)',
    });

    // Clean up after a delay (user may still be sharing)
    setTimeout(() => {
      RNFS.unlink(path).catch(() => {});
    }, 60000);

    return true;
  } catch (err) {
    onError('Could not share CSV. Try again.');
    return false;
  }
}

export async function sharePdfReport(
  entries: ExposureLogEntry[],
  annualTotalMicroSv: number,
  onError: (msg: string) => void
): Promise<boolean> {
  if (Platform.OS === 'web') {
    return false; // Web uses print, not share
  }

  try {
    const { generatePDF } = require('react-native-html-to-pdf');
    const html = generateLogPdfHtml(entries, annualTotalMicroSv);
    const baseName = `fvrad-exposure-${new Date().toISOString().slice(0, 10)}`;

    const file = await generatePDF({
      html,
      fileName: baseName,
      directory: 'Caches',
    });

    const filePath = file.filePath;
    if (!filePath) {
      onError('Could not create PDF.');
      return false;
    }

    const url = Platform.OS === 'ios' ? `file://${filePath}` : `file://${filePath}`;

    await Share.share({
      url,
      message: `FVrad exposure report - Annual: ${(annualTotalMicroSv / 1000).toFixed(2)} mSv`,
      title: 'FVrad Exposure Report (PDF)',
    });

    // Clean up after a delay
    setTimeout(async () => {
      try {
        const RNFS = require('react-native-fs');
        await RNFS.unlink(filePath);
      } catch {
        // ignore
      }
    }, 60000);

    return true;
  } catch (err) {
    onError('Could not share PDF. Try again.');
    return false;
  }
}
